/**
 * URL search-parameter schemas.
 *
 * Filters live in the URL rather than in component state so a filtered list is a
 * link — which is what makes "look at these three DeCAs" something a traffic
 * manager can paste to a colleague, and what makes the browser's back button
 * behave. Every field `.catch()`es, so a hand-edited or stale URL degrades to
 * nothing rather than throwing the screen away.
 *
 * **Every field is optional.** Defaults are applied where the value is read
 * (`PAGE_DEFAULT`, `SORT_DEFAULT`), not in the schema: a required search
 * parameter would force every `<Link to="/decas">` in the portal to restate the
 * default, and the first one that forgot would silently reset the user's sort.
 */

import { z } from 'zod'
import { DECA_ESTADOS } from '@deca/api'

const estadoEnum = z.enum(DECA_ESTADOS)

export const decaSortValues = [
  'createdAt',
  '-createdAt',
  'fecha_realizacion',
  '-fecha_realizacion',
  'documentNumber',
  '-documentNumber',
] as const

export type DecaSort = (typeof decaSortValues)[number]

/**
 * Which document type the list shows (design D1). `todos` reads both endpoints and merges them
 * (`deca/documentRows.ts`); the other two read one endpoint and page on the server.
 *
 * Not an estado and not a column: goods DeCAs and passenger route sheets are different legal
 * instruments under different Orders, served by two parallel APIs, so this picks the *source*
 * rather than filtering one.
 */
export const documentTipoValues = ['todos', 'mercancias', 'viajeros'] as const

export type DocumentTipo = (typeof documentTipoValues)[number]

export const TIPO_DEFAULT: DocumentTipo = 'todos'

export const decaListSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  estado: z.array(estadoEnum).optional().catch(undefined),
  cargadorNif: z.string().optional().catch(undefined),
  transportistaNif: z.string().optional().catch(undefined),
  matricula: z.string().optional().catch(undefined),
  fechaDesde: z.string().optional().catch(undefined),
  fechaHasta: z.string().optional().catch(undefined),
  isGrouped: z.boolean().optional().catch(undefined),
  // #160's «Entrega»: `true` Entregadas, `false` Sin entregar, absent Todas — `listDecas`'s own
  // `entregado`, unchanged.
  entregado: z.boolean().optional().catch(undefined),
  sort: z.enum(decaSortValues).optional().catch(undefined),
  tipo: z.enum(documentTipoValues).optional().catch(undefined),
})

export type DecaListSearch = z.infer<typeof decaListSearchSchema>

/** Selected ids reach the grouping screen through the URL, same reasoning. */
export const groupSearchSchema = z.object({
  ids: z.array(z.string()).optional().catch(undefined),
})

export type GroupSearch = z.infer<typeof groupSearchSchema>

export const pagedSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
})

export type PagedSearch = z.infer<typeof pagedSearchSchema>

/**
 * The «Libreta de direcciones» (#159): a paged search, and `ocultas` for the hidden suggestions in
 * place of the book. In the URL like every other filter, so «the hidden ones» is a link too. `tipo`
 * is the tab (#221), Empresas when absent or unknown.
 */
export const addressBookSearchSchema = pagedSearchSchema.extend({
  ocultas: z.boolean().optional().catch(undefined),
  tipo: z.enum(['empresas', 'lugares', 'vehiculos', 'remolques', 'conductores']).optional().catch(undefined),
})

export type AddressBookSearch = z.infer<typeof addressBookSearchSchema>

export const importSearchSchema = z.object({
  job: z.string().optional().catch(undefined),
})

export type ImportSearch = z.infer<typeof importSearchSchema>

/** Admin tables. The DeCA list has its own, smaller page — see below. */
export const PER_PAGE = 25

/**
 * Ten, as the list frame draws it ("Mostrando 1–10 de 124"). Kept separate from
 * `PER_PAGE` so the admin tables, which Figma does not page at ten, are not
 * changed by a decision made about the document list.
 */
export const DECA_PER_PAGE = 10

export const PAGE_DEFAULT = 1
export const SORT_DEFAULT: DecaSort = '-createdAt'

/**
 * `token` is the capability; `p` only picks a heading. Both `.catch()` to a safe default,
 * so a hand-edited or truncated URL degrades to a readable "this link is not valid"
 * screen rather than throwing the route away — exactly the case a mail client wrapping a
 * long URL, or a copy-paste that drops the query string, produces in practice.
 *
 * `lang` is the invitation's own language, applied once on mount by `ResetPasswordScreen` —
 * see the comment there. Unlike `token` and `p`, it does not `.catch()` to a defined value.
 */
export const resetSearchSchema = z.object({
  token: z.string().catch(''),
  p: z.enum(['invite', 'reset']).catch('reset'),
  // `.optional().catch(undefined)`, like `loginSearchSchema` below, and for a reason beyond taste:
  // a field with a non-undefined default produces a key in the parsed object, and every existing
  // whole-object `toEqual` assertion for `resetSearchSchema` in web/src/test/searchSchemas.test.ts
  // omits `lang`. Catching to `undefined` leaves all of them untouched. Absent means "no preference
  // stated" — the portal's own Spanish default then applies, which is the pre-existing behaviour.
  lang: z.enum(['es', 'en']).optional().catch(undefined),
})

export type ResetSearch = z.infer<typeof resetSearchSchema>

/**
 * Set by `ResetPasswordScreen` on success, so `/login` can show a confirmation.
 *
 * `.optional()`, like every field in this file: a required search parameter would force
 * every existing `navigate({ to: '/login' })` — there are several, none of which care about
 * this flag — to restate `search: { reset: false }` or fail to typecheck. The default is
 * applied where the value is read.
 */
export const loginSearchSchema = z.object({
  reset: z.boolean().optional().catch(undefined),
})

export type LoginSearch = z.infer<typeof loginSearchSchema>
